// Pantalla de una venta o apertura cancelada o vencida: dice por qué y ofrece empezar otra.
import { useNavigate } from '@tanstack/react-router';
import { ArrowRight, UserCheck } from 'lucide-react';
import { Badge, Button } from '@/kit';
import { formatPhone } from '../phoneFormat';
import { TAKEN_OVER_HINT, TAKEN_OVER_MESSAGE } from '../phoneHolder';
import {
    ACCOUNT_OPENING_CLOSED_BODY_PREFIX,
    ACCOUNT_OPENING_CLOSED_BODY_SUFFIX,
    ACCOUNT_OPENING_CLOSED_EYEBROW,
    ACCOUNT_OPENING_CLOSED_SUB,
    ACCOUNT_OPENING_CLOSED_TITLE,
    ACCOUNT_OPENING_EXPIRED_EYEBROW,
    ACCOUNT_OPENING_EXPIRED_SUB,
    ACCOUNT_OPENING_EXPIRED_TITLE,
    ACCOUNT_OPENING_START_NEW,
    ACCOUNT_OPENING_TAKEN_OVER_HINT,
    ACCOUNT_OPENING_HAS_ACCOUNT_BADGE,
    ACCOUNT_OPENING_HAS_ACCOUNT_BODY,
    ACCOUNT_OPENING_HAS_ACCOUNT_EYEBROW,
    ACCOUNT_OPENING_HAS_ACCOUNT_HEADLINE,
    ACCOUNT_OPENING_HAS_ACCOUNT_SCRIPT,
    ACCOUNT_OPENING_HAS_ACCOUNT_SCRIPT_LABEL,
    ACCOUNT_OPENING_HAS_ACCOUNT_SUB,
    ACCOUNT_OPENING_HAS_ACCOUNT_TITLE,
    ACCOUNT_OPENING_SELL_TO_CUSTOMER,
} from '../accountOpening';
import { StateIcon } from './KycScreenBits';
import { WizardCard, WizardCardBody, WizardCardHead } from './WizardCard';
import { WizardFootNav } from './WizardFootNav';

export function SaleClosedNotice({
    phone,
    expired = false,
    quoteDiscarded = false,
    takenOver = false,
    alreadyHasAccount = false,
    accountOpening = false,
}: {
    phone: string;
    expired?: boolean;
    quoteDiscarded?: boolean;
    takenOver?: boolean;
    /** Apertura cerrada porque el cliente ya tiene cuenta PagoConFiado. */
    alreadyHasAccount?: boolean;
    /** La sesión es una apertura de cuenta: el aviso no habla de venta. */
    accountOpening?: boolean;
}) {
    const navigate = useNavigate();

    if (alreadyHasAccount) return <AlreadyHasAccountNotice phone={phone} />;
    if (accountOpening) {
        return <AccountOpeningClosedNotice phone={phone} expired={expired} takenOver={takenOver} />;
    }

    return (
        <WizardCard>
            {takenOver ? (
                <WizardCardHead
                    eyebrow="Venta cancelada"
                    title={TAKEN_OVER_MESSAGE}
                    sub={TAKEN_OVER_HINT}
                />
            ) : quoteDiscarded ? (
                <WizardCardHead
                    eyebrow="Cotización vencida"
                    title="La cotización de esta venta venció"
                    sub="El crédito se descartó y la venta se cerró. Puedes iniciar una venta nueva o empezarla de nuevo desde la ficha del prospecto, si la guardaste."
                />
            ) : expired ? (
                <WizardCardHead
                    eyebrow="Venta vencida"
                    title="Esta venta venció por inactividad"
                    sub="Puedes iniciar una venta nueva o empezarla de nuevo desde la ficha del prospecto, si la guardaste."
                />
            ) : (
                <WizardCardHead
                    eyebrow="Venta cancelada"
                    title="Esta venta ya está cancelada"
                    sub="No se puede retomar: ni el código ni los pasos siguientes van a responder."
                />
            )}

            {!expired && !quoteDiscarded && !takenOver && (
                <WizardCardBody>
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] leading-relaxed text-destructive">
                        La venta del teléfono{' '}
                        <span className="font-medium tabular-nums">{formatPhone(phone)}</span> se
                        canceló. Puedes iniciar otra con el mismo número.
                    </div>
                </WizardCardBody>
            )}

            <WizardFootNav showExit={false}>
                <Button
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void navigate({ to: '/wizard' })}
                >
                    Iniciar una venta nueva
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}

/** La apertura no abre una segunda cuenta: ofrece venderle al cliente con su número. */
function AlreadyHasAccountNotice({ phone }: { phone: string }) {
    const navigate = useNavigate();
    return (
        <WizardCard>
            <WizardCardHead
                eyebrow={ACCOUNT_OPENING_HAS_ACCOUNT_EYEBROW}
                title={ACCOUNT_OPENING_HAS_ACCOUNT_TITLE}
                sub={ACCOUNT_OPENING_HAS_ACCOUNT_SUB}
                right={
                    <Badge variant="secondary" className="whitespace-nowrap">
                        {ACCOUNT_OPENING_HAS_ACCOUNT_BADGE}
                    </Badge>
                }
            />
            <WizardCardBody>
                <div className="text-center">
                    <StateIcon tone="info">
                        <UserCheck className="size-7" aria-hidden />
                    </StateIcon>
                    <p className="mb-1 text-[15px] font-semibold">
                        {ACCOUNT_OPENING_HAS_ACCOUNT_HEADLINE}
                    </p>
                    <p className="mx-auto mb-5 max-w-md text-[12.5px] leading-relaxed text-muted-foreground">
                        {ACCOUNT_OPENING_HAS_ACCOUNT_BODY}
                    </p>
                    <div className="mx-auto max-w-md rounded-lg border border-border bg-muted/30 p-4 text-left">
                        <p className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
                            {ACCOUNT_OPENING_HAS_ACCOUNT_SCRIPT_LABEL}
                        </p>
                        <p className="text-[12.5px] leading-relaxed">
                            {ACCOUNT_OPENING_HAS_ACCOUNT_SCRIPT}
                        </p>
                    </div>
                </div>
            </WizardCardBody>
            <WizardFootNav showExit={false}>
                <Button
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() => void navigate({ to: '/wizard', search: { phone } })}
                >
                    {ACCOUNT_OPENING_SELL_TO_CUSTOMER}
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}

/** Apertura cancelada, vencida o tomada por otra sucursal: la salida es abrir otra. */
function AccountOpeningClosedNotice({
    phone,
    expired,
    takenOver,
}: {
    phone: string;
    expired: boolean;
    takenOver: boolean;
}) {
    const navigate = useNavigate();
    return (
        <WizardCard>
            {takenOver ? (
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENING_CLOSED_EYEBROW}
                    title={TAKEN_OVER_MESSAGE}
                    sub={ACCOUNT_OPENING_TAKEN_OVER_HINT}
                />
            ) : expired ? (
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENING_EXPIRED_EYEBROW}
                    title={ACCOUNT_OPENING_EXPIRED_TITLE}
                    sub={ACCOUNT_OPENING_EXPIRED_SUB}
                />
            ) : (
                <WizardCardHead
                    eyebrow={ACCOUNT_OPENING_CLOSED_EYEBROW}
                    title={ACCOUNT_OPENING_CLOSED_TITLE}
                    sub={ACCOUNT_OPENING_CLOSED_SUB}
                />
            )}

            {!expired && !takenOver && (
                <WizardCardBody>
                    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-[12px] leading-relaxed text-destructive">
                        {ACCOUNT_OPENING_CLOSED_BODY_PREFIX}{' '}
                        <span className="font-medium tabular-nums">{formatPhone(phone)}</span>{' '}
                        {ACCOUNT_OPENING_CLOSED_BODY_SUFFIX}
                    </div>
                </WizardCardBody>
            )}

            <WizardFootNav showExit={false}>
                <Button
                    rightIcon={<ArrowRight className="size-3.5" aria-hidden />}
                    onClick={() =>
                        void navigate({ to: '/wizard', search: { accountOpening: true } })
                    }
                >
                    {ACCOUNT_OPENING_START_NEW}
                </Button>
            </WizardFootNav>
        </WizardCard>
    );
}
